/* eslint eslint-comments/no-use: off */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker, type ItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {getQueryClient} from '@github-ui/query-client'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {LockIcon, RepoIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, type SelectPanelProps} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import {type Environment, fetchQuery, graphql, useRelayEnvironment} from 'react-relay'

import {getRepositorySearchQuery} from '../utils/get-repository-search-query'
import type {
  RepositoryPickerSearchRepositoriesQuery,
  RepositoryPickerSearchRepositoriesQuery$data,
} from './__generated__/RepositoryPickerSearchRepositoriesQuery.graphql'
import type {
  RepositoryPickerTopRepositoriesQuery,
  RepositoryPickerTopRepositoriesQuery$data,
} from './__generated__/RepositoryPickerTopRepositoriesQuery.graphql'
import {
  fetchPossibleTransferRepositories,
  type IssueTransferContext,
  possibleTransferRepositoriesQueryKey,
} from './fetch-possible-transfer-repositories'
import {
  fetchRepositoryFromServer as fetchRepositoryFromServerQuery,
  repositoryQueryKey as repositoryQueryKeyFromServer,
} from './fetch-repository'
import type {
  RepositoryPickerRepository as RepositoryPickerRepositoryType,
  RepositoryVisibility as RepositoryVisibilityType,
} from './repository-picker-types'
import styles from './RepositoryPicker.module.css'

export type RepositoryPickerRepository = RepositoryPickerRepositoryType
export type RepositoryVisibility = RepositoryVisibilityType

export type RepositoryPickerTopRepositoriesViewer = RepositoryPickerTopRepositoriesQuery$data['viewer']

export function readTopRepositories(viewer: RepositoryPickerTopRepositoriesViewer): RepositoryPickerRepository[] {
  const topRepositories = (viewer.topRepositories.edges || []).flatMap(a => (a?.node ? [a.node] : []))

  // RELAY-CLEANUP(RepositoryPickerTopRepositoriesQuery): cast for Relay's open-enum visibility variant.
  return topRepositories as RepositoryPickerRepository[]
}

export type RepositoryPickerTestQueries = {
  TopRepoQuery: RepositoryPickerTopRepositoriesQuery
}

export type RepositoryPickerProps = {
  initialRepository: RepositoryPickerRepository | undefined
  topRepositories: RepositoryPickerRepository[] | null
  onSelect: (repo: RepositoryPickerRepository | undefined) => void
  preventDefault?: boolean
  organization?: string
  focusRepositoryPicker?: boolean
  enforceAtleastOneSelected?: boolean
  options?: {hasIssuesEnabled?: boolean; readonly?: boolean; includeForks?: boolean}
  renderTrailingVisual?: (repoId: string) => React.ReactElement | undefined
  exclude?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  title?: string
  subtitle?: string | React.ReactElement
  preventClose?: boolean
  triggerOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  ignoredRepositories?: string[]
  repositoryFilter?: (repo: RepositoryPickerRepository) => boolean
  customNoResultsTitle?: string
  customNoResultsItem?: React.ReactElement
  repoNameOnly?: boolean
  pickerId?: string
  currentRepoVisibility?: RepositoryVisibility
  issueTransferContext?: IssueTransferContext
  // temporary, this will go away when fully ship the primer_react_select_panel_fullscreen_on_narrow FF
  responsiveOnNarrow?: boolean
  notice?: SelectPanelProps['notice']
  hideContainer?: boolean
} & Pick<ItemPickerProps<ExtendedItemProps<RepositoryPickerRepository>>, 'insidePortal'>

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TopRepositories = graphql`
  query RepositoryPickerTopRepositoriesQuery(
    $topRepositoriesFirst: Int = 10
    $hasIssuesEnabled: Boolean
    $owner: String = null
  ) {
    viewer {
      topRepositories(
        first: $topRepositoriesFirst
        hasIssuesEnabled: $hasIssuesEnabled
        orderBy: {field: UPDATED_AT, direction: DESC}
        owner: $owner
      ) {
        edges {
          node {
            id
            databaseId
            name
            nameWithOwner
            owner {
              databaseId
              login
              avatarUrl(size: 64)
              issueTypesEnabled
            }
            isLocked
            isPrivate
            isOwnerEnterpriseManaged
            visibility
            isArchived
            isInOrganization
            hasIssuesEnabled
            hasAnyTemplates
            slashCommandsEnabled
            viewerCanPush
            isBlankIssuesEnabled
            viewerCanCreateIssues
            viewerInteractionLimitReasonHTML(action: "create an issue")
            viewerIssueCreationPermissions {
              labelable
              milestoneable
              assignable
              triageable
              typeable
              canSetIssueFields
            }
            shortDescriptionHTML
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const SearchRepositories = graphql`
  query RepositoryPickerSearchRepositoriesQuery($searchQuery: String!, $after: String) {
    search(query: $searchQuery, type: REPOSITORY, first: 10, after: $after) {
      repositoryCount
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        ... on Repository {
          id
          databaseId
          name
          nameWithOwner
          owner {
            databaseId
            login
            avatarUrl(size: 64)
            issueTypesEnabled
          }
          isLocked
          isPrivate
          isOwnerEnterpriseManaged
          visibility
          isArchived
          isInOrganization
          hasIssuesEnabled
          hasAnyTemplates
          slashCommandsEnabled
          viewerCanPush
          isBlankIssuesEnabled
          viewerCanCreateIssues
          viewerInteractionLimitReasonHTML(action: "create an issue")
          viewerIssueCreationPermissions {
            labelable
            milestoneable
            assignable
            triageable
            typeable
            canSetIssueFields
          }
          shortDescriptionHTML
        }
      }
    }
  }
`

export async function fetchTopRepositories(
  environment: Environment,
  options: {
    first?: number
    hasIssuesEnabled?: boolean
    owner?: string
  } = {},
): Promise<RepositoryPickerRepository[]> {
  const {first = 10, hasIssuesEnabled, owner} = options
  const queryData = await fetchQuery<RepositoryPickerTopRepositoriesQuery>(
    environment,
    TopRepositories,
    {
      topRepositoriesFirst: first,
      hasIssuesEnabled,
      owner,
    },
    {fetchPolicy: 'store-or-network'},
  ).toPromise()

  if (queryData?.viewer == null) {
    return []
  }

  return readTopRepositories(queryData.viewer)
}

export async function fetchRepository(
  _environment: Environment,
  owner: string,
  name: string,
): Promise<RepositoryPickerRepository | null> {
  return fetchRepositoryFromServerQuery(owner, name)
}

export function repositoryQueryKey(owner: string, name: string) {
  return repositoryQueryKeyFromServer(owner, name)
}

export async function fetchRepositoryFromServer(owner: string, name: string, signal?: AbortSignal) {
  return fetchRepositoryFromServerQuery(owner, name, signal)
}

export function RepositoryPicker({
  initialRepository,
  onSelect,
  preventDefault,
  organization,
  topRepositories,
  focusRepositoryPicker,
  enforceAtleastOneSelected,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  options: {hasIssuesEnabled, readonly, includeForks} = {
    hasIssuesEnabled: undefined,
    readonly: false,
    includeForks: false,
  },
  renderTrailingVisual,
  exclude,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  anchorElement,
  title,
  subtitle,
  preventClose,
  triggerOpen,
  onOpen,
  onClose,
  ignoredRepositories,
  repositoryFilter,
  customNoResultsItem,
  customNoResultsTitle,
  repoNameOnly,
  pickerId,
  currentRepoVisibility,
  issueTransferContext,
  responsiveOnNarrow,
  notice,
  insidePortal,
  hideContainer,
}: RepositoryPickerProps) {
  const {addToast} = useToastContext()
  const [initiallySelectedRepo, _] = useState<RepositoryPickerRepository | undefined>(initialRepository)
  const [filter, setFilter] = useState('')
  const [searchResults, setSearchResults] = useState<RepositoryPickerRepository[] | undefined>(undefined)
  const [searchLoading, setSearchLoading] = useState<boolean>(topRepositories == null)
  // // We need to keep track of the filter value in a ref because the subscribe function captures the filter value at the time of creation and doesn't update when the state changes.
  const filterRef = useRef(filter)

  // Clear loading after initial data preloaded
  const [previousTopRepositories, setPreviousTopRepositories] = useState(topRepositories)
  if (topRepositories !== previousTopRepositories) {
    setPreviousTopRepositories(topRepositories)
    if (topRepositories != null) {
      setSearchLoading(false)
    }
  }

  const relayEnvironment = useRelayEnvironment()
  const fetchPossibleTransferRepos = useCallback(
    async (searchQuery: string) => {
      if (!issueTransferContext) return
      if (searchQuery.trim() === '') {
        setSearchResults(undefined)
        setSearchLoading(false)
        return
      }
      setSearchLoading(true)
      try {
        const fetchedRepos = await getQueryClient().fetchQuery({
          queryKey: possibleTransferRepositoriesQueryKey(issueTransferContext, searchQuery),
          queryFn: ({signal}) => fetchPossibleTransferRepositories(issueTransferContext, searchQuery, signal),
        })

        const currentFilter = filterRef.current
        if (searchQuery.trim() === currentFilter.trim()) {
          setSearchResults(fetchedRepos as RepositoryPickerRepository[])
          setSearchLoading(false)
        }
      } catch {
        setSearchLoading(false)
      }
    },
    [issueTransferContext],
  )

  const fetchRepos = useCallback(
    (searchQuery: string, orgs: string[]) => {
      if (searchQuery.trim() === '') {
        setSearchResults(undefined)
        setSearchLoading(false)
        return
      }
      // run query on search
      setSearchLoading(true)
      fetchQuery<RepositoryPickerSearchRepositoriesQuery>(relayEnvironment, SearchRepositories, {
        searchQuery: getRepositorySearchQuery(searchQuery, {
          organization,
          excludeRepo: exclude,
          includeForks,
          repositoryVisibility: currentRepoVisibility,
        }),
      }).subscribe({
        next: (data: RepositoryPickerSearchRepositoriesQuery$data) => {
          if (data !== null) {
            // RELAY-CLEANUP(RepositoryPickerSearchRepositoriesQuery): cast needed because
            // inline spread `... on Repository` makes fields optional in Relay types.
            // Remove when this query leaves Relay.
            let fetchedRepos = (data.search.nodes || []).flatMap(node =>
              node?.id ? [node as RepositoryPickerRepository] : [],
            )

            fetchedRepos = fetchedRepos.filter(repo => !repo.isLocked && !repo.isArchived)

            if (hasIssuesEnabled) {
              fetchedRepos = fetchedRepos.filter(repo => repo.hasIssuesEnabled === hasIssuesEnabled)
            }

            let sortedNextSearchResults = fetchedRepos.sort((a, b) => {
              if (orgs.includes(a.owner.login) && !orgs.includes(b.owner.login)) {
                return -1
              } else if (!orgs.includes(a.owner.login) && orgs.includes(b.owner.login)) {
                return 1
              } else {
                return a.owner.login.localeCompare(b.owner.login)
              }
            })

            if (ignoredRepositories) {
              const ignoredSet = new Set(ignoredRepositories)
              sortedNextSearchResults = sortedNextSearchResults.filter(repo => !ignoredSet.has(repo.nameWithOwner))
            }
            if (repositoryFilter) {
              sortedNextSearchResults = sortedNextSearchResults.filter(repositoryFilter)
            }
            const currentFilter = filterRef.current
            // Only update the search results if the search query is the same as the current filter.
            // We don't need to render each search result if the user has already changed the search query.
            if (searchQuery.trim() === currentFilter.trim()) {
              setSearchResults(sortedNextSearchResults)
              setSearchLoading(false)
            }
          }
        },
        error: () => {
          setSearchLoading(false)
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotSearchRepositories,
          })
        },
      })
    },
    [
      relayEnvironment,
      organization,
      exclude,
      includeForks,
      currentRepoVisibility,
      hasIssuesEnabled,
      ignoredRepositories,
      repositoryFilter,
      addToast,
    ],
  )

  const fetchedRepos = useMemo(() => {
    let nodes = topRepositories || []

    if (initiallySelectedRepo) {
      if (!nodes.find(node => node.id === initiallySelectedRepo.id)) {
        nodes = [initiallySelectedRepo, ...nodes]
      }
    }

    if (initialRepository) {
      if (!nodes.find(node => node.id === initialRepository.id)) {
        nodes = [initialRepository, ...nodes]
      }
    }

    if (organization) {
      nodes = nodes.filter(repo => repo.owner.login === organization)
    }

    if (exclude) {
      nodes = nodes.filter(repo => repo.nameWithOwner !== exclude)
    }

    if (ignoredRepositories) {
      const ignoredSet = new Set(ignoredRepositories)
      nodes = nodes.filter(repo => !ignoredSet.has(repo.nameWithOwner))
    }

    return nodes.slice(0, 10)
  }, [topRepositories, initiallySelectedRepo, initialRepository, organization, exclude, ignoredRepositories])

  const knownOrgs = useMemo(() => [...new Set(fetchedRepos.map(repo => repo.owner.login))], [fetchedRepos])

  const debounceFetchRepo = useDebounce((nextValue: string) => {
    if (issueTransferContext) {
      fetchPossibleTransferRepos(nextValue)
    } else {
      fetchRepos(nextValue, knownOrgs)
    }
  }, VALUES.pickerDebounceTime)

  const getItemKey = useCallback((o: RepositoryPickerRepository) => o.id, [])

  const repositoryPickerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (focusRepositoryPicker) repositoryPickerRef.current?.focus()
  }, [repositoryPickerRef, focusRepositoryPicker])

  const renderAnchor = useCallback(
    ({...anchorProps}: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      const repositoryValue = initialRepository
        ? repoNameOnly
          ? initialRepository.name
          : initialRepository.nameWithOwner
        : 'No repository selected'

      const label = initialRepository ? `Selected repository: ${repositoryValue}` : LABELS.selectRepository

      return (
        <Button
          leadingVisual={RepoIcon}
          trailingVisual={TriangleDownIcon}
          aria-label={label}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          {...anchorProps}
          disabled={readonly}
          ref={repositoryPickerRef}
        >
          {initialRepository ? (
            <span>
              {repoNameOnly ? initialRepository.name : `${initialRepository.owner.login}/${initialRepository.name}`}
            </span>
          ) : (
            LABELS.selectRepository
          )}
        </Button>
      )
    },
    [anchorElement, ariaDescribedBy, ariaLabelledBy, initialRepository, readonly, repoNameOnly],
  )

  const convertToItemProps = useCallback(
    (repo: RepositoryPickerRepository): ExtendedItemProps<RepositoryPickerRepository> => ({
      // this is a hack to make sure that we are using the prop
      id: `${repo.id}_${repo.databaseId}_${repo.slashCommandsEnabled}`,
      text: repoNameOnly ? repo.name : `${repo.owner.login}/${repo.name}`,
      source: repo,
      leadingVisual: () => (repo.isPrivate ? <LockIcon size={12} /> : <RepoIcon size={12} />),
      trailingVisual: renderTrailingVisual?.(repo.id),
      className: styles.Item,
    }),
    [renderTrailingVisual, repoNameOnly],
  )

  const items = useMemo(() => {
    if (searchResults) return searchResults
    const filteredItems = fetchedRepos.filter(repo => !repo.isArchived && !repo.isLocked)
    if (repositoryFilter) {
      return filteredItems.filter(repositoryFilter)
    }
    return filteredItems
  }, [fetchedRepos, repositoryFilter, searchResults])

  const autoSelectFirstRepo = useEffectEvent(() => {
    if (!initialRepository && items.length > 0 && !preventDefault) {
      onSelect(items[0])
    }
  })

  useEffect(() => {
    // automatically select the first repo
    autoSelectFirstRepo()
  }, [])

  const filterItems = useCallback(
    (value: string) => {
      if (readonly) return
      const trimmedValue = value.trim()
      // Only run search if the value has changed
      if (filter === trimmedValue) return
      debounceFetchRepo(trimmedValue)
      setFilter(trimmedValue)
      filterRef.current = trimmedValue
    },
    [readonly, filter, debounceFetchRepo],
  )

  const showNoMatchMessage = useMemo(() => items.length === 0, [items.length])
  const message = useMemo<SelectPanelProps['message'] | undefined>(
    () =>
      showNoMatchMessage
        ? {
            title: customNoResultsTitle ?? 'No repositories match',
            body: customNoResultsItem ?? 'Try searching with a different query for results.',
            variant: 'empty',
          }
        : undefined,
    [showNoMatchMessage, customNoResultsTitle, customNoResultsItem],
  )

  return (
    <div
      className={clsx(styles.repositoryPickerContainer, hideContainer && styles.hideContainer)}
      data-testid="repository-picker-container"
    >
      <ItemPicker
        items={items}
        initialSelectedItems={initialRepository ? [initialRepository] : []}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.selectRepository}
        selectionVariant="single"
        onSelectionChange={([firstRepo]) => {
          return onSelect(firstRepo)
        }}
        loading={searchLoading}
        renderAnchor={renderAnchor}
        selectPanelRef={repositoryPickerRef}
        enforceAtleastOneSelected={enforceAtleastOneSelected}
        resultListAriaLabel={'Repository results'}
        height={'large'}
        width={'medium'}
        title={title}
        subtitle={subtitle}
        preventClose={preventClose}
        triggerOpen={triggerOpen}
        onOpen={onOpen}
        onClose={onClose}
        pickerId={pickerId}
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={message}
        notice={notice}
        responsiveOnNarrow={responsiveOnNarrow}
        insidePortal={insidePortal}
      />
    </div>
  )
}
